import React, { useState, useEffect } from 'react';
import { Idea, Priority, Topic } from '../types';
import { X } from 'lucide-react';

interface IdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (idea: Partial<Idea>) => Promise<void>;
  editingIdea?: Idea | null;
  topics: Topic[];
  onAddTopic: (name: string) => Promise<void>;
}

export const IdeaModal: React.FC<IdeaModalProps> = ({ 
  isOpen, onClose, onSave, editingIdea, topics, onAddTopic 
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>(Priority.MEDIUM);
  const [topicId, setTopicId] = useState('');
  const [newTopicName, setNewTopicName] = useState('');
  const [isAddingTopic, setIsAddingTopic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // FIXED: Removed 'topics' from dependency array to prevent form reset when a new topic is added
  useEffect(() => {
    if (isOpen) {
        if (editingIdea) {
          setTitle(editingIdea.title);
          setDescription(editingIdea.description);
          setPriority(editingIdea.priority);
          setTopicId(editingIdea.topicId);
        } else {
          setTitle('');
          setDescription('');
          setPriority(Priority.MEDIUM);
          // Only set default topic if topicId isn't already set (preserves selection if topics reload)
          setTopicId(prev => prev || (topics.length > 0 ? topics[0].id : ''));
        }
    }
  }, [editingIdea, isOpen]); 

  // Watch for new topics to auto-select the new one
  useEffect(() => {
    if (topics.length > 0 && !topicId) {
        setTopicId(topics[0].id);
    }
  }, [topics, topicId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;

    setIsSaving(true);
    await onSave({
      id: editingIdea?.id,
      title,
      description,
      priority,
      topicId: topicId || (topics.length > 0 ? topics[0].id : ''),
    });
    setIsSaving(false);
    onClose();
  };

  const handleAddNewTopic = async () => {
    if (!newTopicName) return;
    await onAddTopic(newTopicName);
    setNewTopicName('');
    setIsAddingTopic(false);
    // Note: The new topic will be added to the list via props update
    // We can rely on the user to select it or add logic to auto-select the last added topic if needed
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 animate-fade-in">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-bold text-secondary">
            {editingIdea ? 'Edit Idea' : 'New Idea'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              required
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white text-gray-900 placeholder-gray-400"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="E.g., Top 10 React Tips"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
            <textarea
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none h-28 resize-none bg-white text-gray-900 placeholder-gray-400"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short brief about the content..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Priority</label>
              <select
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white text-gray-900"
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
              >
                {Object.values(Priority).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Topic</label>
              {isAddingTopic ? (
                <div className="flex space-x-2">
                  <input
                    type="text"
                    autoFocus
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-900"
                    value={newTopicName}
                    onChange={(e) => setNewTopicName(e.target.value)}
                    placeholder="Topic name"
                  />
                  <button
                    type="button"
                    onClick={handleAddNewTopic}
                    className="px-3 py-1 bg-secondary text-white rounded-lg text-xs hover:bg-blue-800 transition-colors"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <select
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white text-gray-900"
                    value={topicId}
                    onChange={(e) => setTopicId(e.target.value)}
                  >
                    {topics.length === 0 && <option value="">No topics</option>}
                    {topics.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <button 
                    type="button" 
                    onClick={() => setIsAddingTopic(true)}
                    className="text-primary text-xs whitespace-nowrap hover:underline font-medium"
                  >
                    + New
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-red-600 disabled:opacity-50 font-medium transition-colors shadow-sm"
            >
              {isSaving ? 'Saving...' : 'Save Idea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};